import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Code, CodeLinks, Field, Prose, Section } from './DetailPrimitives';
import { conceptHref, docClassLabel, formatDate, refTypeLabel } from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

/**
 * A source document: what it is, who issued it, and how to open it.
 *
 * REGISTRY ONLY — this records what a document is and how to find it, never the bytes.
 * What it REACHES is the impact panel's job, deliberately kept separate the same way
 * OntologyDetailPage splits "what this means" from "what this reaches".
 */

/**
 * Where the document actually lives.
 *
 * Three locator shapes, and only two of them can be opened from here:
 *
 *   - `url` — published guidance, the common case. A plain external link.
 *   - `inline` — the text is held in the registry; there is nothing to fetch.
 *   - `integration` — the institution's own PDF in its Drive or bucket. Resolving that
 *     needs the org's integration binding and service-account impersonation, which the
 *     control plane has not ported (spec 8.6). The object is NAMED and the action is
 *     disabled with a reason, rather than offered as a link that would 404: a compliance
 *     officer needs to know the document is registered and where, even while the viewer
 *     is missing.
 */
const OpenDocument: React.FC<{ document: Record<string, any> }> = ({ document }) => {
    const locator = document.locator || {};
    const url = locator.url || document.source_link;

    if (locator.kind === 'integration') {
        const name = locator.object_name || locator.objectName;
        return (
            <Tooltip
                title="Stored in your organisation's document integration. Opening it from here is not available yet."
                placement="top"
            >
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        px: 1, py: 0.5, borderRadius: '4px',
                        border: `1px dashed ${dsVar('line')}`, backgroundColor: dsVar('page'),
                        color: dsVar('fg-muted'), fontSize: '0.8125rem', cursor: 'not-allowed',
                    }}
                >
                    <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />
                    {name || 'Stored document'}
                </Box>
            </Tooltip>
        );
    }

    if (locator.kind === 'inline') {
        return (
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>
                Held in the registry — there is no separate file.
            </Typography>
        );
    }

    if (!url) return null;

    return (
        <Box
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                fontSize: '0.8125rem', color: dsVar('accent-fg'), textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
            }}
        >
            Open document <LaunchIcon sx={{ fontSize: 13 }} />
        </Box>
    );
};

const DocumentDetailBody: React.FC<{ document: Record<string, any> }> = ({ document }) => (
    <Box>
        {document.summary && <Prose>{document.summary}</Prose>}

        <Section title="The document">
            {/* The reference printed ON the instrument. Distinct from source_doc_code,
                which is this system's identity for it and means nothing off-platform. */}
            <Field
                label="Identifier"
                value={document.document_identifier && <Code>{document.document_identifier}</Code>}
            />
            <Field label="Issued by" value={document.issuer} />
            <Field label="Jurisdiction" value={document.jurisdiction} />
            <Field label="Regime" value={document.regime} />
            <Field label="Type" value={refTypeLabel(document.ref_type)} />
            <Field label="Origin" value={docClassLabel(document.doc_class)} />
            <Field label="Version" value={document.version_label} />
            <Field
                label="Issued"
                value={document.issuance_date ? formatDate(document.issuance_date) : null}
            />
        </Section>

        <Section title="Where to read it" always>
            <OpenDocument document={document} />
        </Section>

        {/* The document's own forward declaration, and the reason a document with no
            citations still has a blast radius. Rendered here as well as in the impact
            panel because it is a property of the document, not only a route into it. */}
        {Array.isArray(document.governs_concepts) && document.governs_concepts.length > 0 && (
            <Section title="Terms it names">
                <CodeLinks codes={document.governs_concepts} href={conceptHref} />
            </Section>
        )}
    </Box>
);

export default DocumentDetailBody;
